import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { allMovies, categories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'hot'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const isAll = slug === 'all';

  const filteredMovies = isAll
    ? allMovies
    : allMovies.filter((m) => m.categorySlug === slug);

  const sortedMovies = [...filteredMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'hot') return (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0);
    return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
  });

  const parentCategories = categories.filter((c) => c.slug !== 'all');

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-4">
          <div className="max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">{currentCategory.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="px-4 md:px-6 py-4 border-b border-background-200/60">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    (slug === cat.slug || (isAll && cat.slug === 'all'))
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950 font-medium'
                      : 'text-foreground-500 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="px-4 md:px-6 py-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between">
              <p className="text-sm text-foreground-400">
                共找到 <strong className="text-foreground-600">{sortedMovies.length}</strong> 部影片
              </p>
              <div className="flex items-center gap-2">
                {([
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                  { key: 'hot', label: '热门' },
                ] as const).map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setSortBy(item.key)}
                    className={`px-3 py-1.5 text-xs rounded-md transition-all duration-200 cursor-pointer ${
                      sortBy === item.key
                        ? 'bg-secondary-500 text-background-50 dark:text-foreground-950 font-medium'
                        : 'text-foreground-500 hover:text-foreground-950 hover:bg-background-100'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Movie Grid */}
        <section className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-[1400px] mx-auto">
            {sortedMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {sortedMovies.map((movie) => (
                  <MovieCard key={`cat-${movie.id}`} movie={movie} variant="poster" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-movie-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-400 text-sm">该分类暂无影片，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>

        {/* Sub-categories section */}
        {sortedMovies.length > 0 && (
          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
            <div className="max-w-[1400px] mx-auto">
              <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                探索更多分类
              </h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-11 gap-3">
                {parentCategories
                  .filter((c) => !(slug && c.slug === slug))
                  .map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex flex-col items-center gap-2 p-3 rounded-lg bg-background-50 hover:bg-background-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10">
                        <i className={`${cat.icon} text-primary-500 text-base`}></i>
                      </div>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{cat.name}</span>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Schema.org BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://7777cinema.com/' },
              { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://7777cinema.com/category/${slug}` },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `7777影院 - ${currentCategory.name}`,
            description: `7777影院${currentCategory.name}分类，提供海量高清${currentCategory.name}资源，免费在线观看。`,
          }),
        }}
      />

      <Footer />
    </>
  );
}